import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';

import type {
    AlterarCriticidadeRequest,
    AnotarSolicitacaoRequest,
    ArquivadasPageResponse,
    ArquivadasResumoResponse,
    FotoResponsavel,
    HistoricoAndamento,
    KanbanCriticidade,
    KanbanSolicitacao,
    KanbanStatus,
    MoverSolicitacaoRequest,
    MoverSolicitacaoResponse,
    Responsavel
} from './kanban-servicos.model';

@Injectable({ providedIn: 'root' })
export class KanbanServicosService extends BaseService {
    readonly PATH = SICOM_PATHS.servicos.kanban;

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    listarStatus(context?: HttpContext): Observable<KanbanStatus[]> {
        return this.http.get<KanbanStatus[]>(`${this.PATH}/status`, { context });
    }

    listarSolicitacoes(idServico: number, uf: string, context?: HttpContext): Observable<KanbanSolicitacao[]> {
        const params = new HttpParams().set('idServico', idServico).set('uf', uf);
        return this.http.get<KanbanSolicitacao[]>(`${this.PATH}/solicitacoes`, { params, context });
    }

    /**
     * Protocolos concluídos do serviço na UF nos últimos 30 dias (janela fixa no backend), já com os
     * dados do portal.
     *
     * Existe porque a coluna conclusiva do quadro não pode se alimentar da listagem de protocolos
     * como as demais: concluir tira o protocolo de lá (a listagem traz só os vivos, `A` e `T`), e o
     * card sumia em vez de aparecer em Concluído. Aqui a tabela de controle é quem lista.
     *
     * O retorno é o objeto **cru do portal**, e não um DTO nosso: é o mesmo formato da listagem viva,
     * então quem consome passa pelo mesmo `mapearItemStatusProtocolo`. Protocolo cuja consulta ao
     * portal falhar volta só com número, ano e UF — o card sai mínimo, mas sai.
     */
    listarConcluidos(idServico: number, uf: string, context?: HttpContext): Observable<Record<string, unknown>[]> {
        const params = new HttpParams().set('idServico', idServico).set('uf', uf);
        return this.http.get<Record<string, unknown>[]>(`${this.PATH}/solicitacoes/concluidas`, { params, context });
    }

    mover(body: MoverSolicitacaoRequest, context?: HttpContext): Observable<MoverSolicitacaoResponse> {
        return this.http.put<MoverSolicitacaoResponse>(`${this.PATH}/solicitacoes/mover`, body, { context });
    }

    /** Acrescenta uma linha a tramitacao local de um protocolo ja rastreado. */
    anotar(body: AnotarSolicitacaoRequest, context?: HttpContext): Observable<void> {
        return this.http.post<void>(`${this.PATH}/solicitacoes/anotacao`, body, { context });
    }

    /**
     * HTML do e-mail guardado numa anotacao. `responseType: 'text'` porque o corpo e o HTML cru — o
     * backend o devolve como texto, e o Angular nao tenta interpretar nada dele.
     */
    obterEmailDaAnotacao(idAnotacao: number, context?: HttpContext): Observable<string> {
        return this.http.get(`${this.PATH}/solicitacoes/anotacao/${idAnotacao}/email`, { responseType: 'text', context });
    }

    listarHistorico(ano: number, numero: number, context?: HttpContext): Observable<HistoricoAndamento[]> {
        const params = new HttpParams().set('ano', ano).set('numero', numero);
        return this.http.get<HistoricoAndamento[]>(`${this.PATH}/solicitacoes/historico`, { params, context });
    }

    /** Fotos só dos responsáveis atribuídos: IM_FOTO é um LOB e a lista de elegíveis traz a UF inteira. */
    listarFotosResponsaveis(ids: number[], context?: HttpContext): Observable<FotoResponsavel[]> {
        const params = new HttpParams().appendAll({ ids });
        return this.http.get<FotoResponsavel[]>(`${this.PATH}/responsaveis/fotos`, { params, context });
    }

    listarCriticidades(context?: HttpContext): Observable<KanbanCriticidade[]> {
        return this.http.get<KanbanCriticidade[]>(`${this.PATH}/criticidades`, { context });
    }

    listarResponsaveis(uf: string, context?: HttpContext): Observable<Responsavel[]> {
        const params = new HttpParams().set('uf', uf);
        return this.http.get<Responsavel[]>(`${this.PATH}/responsaveis`, { params, context });
    }

    /** Altera so a criticidade: nao move o card, nao gera historico e nao tramita no portal. */
    alterarCriticidade(body: AlterarCriticidadeRequest, context?: HttpContext): Observable<MoverSolicitacaoResponse> {
        return this.http.put<MoverSolicitacaoResponse>(`${this.PATH}/solicitacoes/criticidade`, body, { context });
    }

    /** Página do arquivo: concluídos que saíram da janela de 30 dias do quadro. */
    listarArquivadas(idServico: number, uf: string, pagina: number, tamanho: number, numero?: number, context?: HttpContext): Observable<ArquivadasPageResponse> {
        let params = new HttpParams().set('idServico', idServico).set('uf', uf).set('pagina', pagina).set('tamanho', tamanho);
        // `numero` só viaja quando há busca: enviado vazio, o backend leria string em branco como filtro.
        if (numero != null) {
            params = params.set('numero', numero);
        }
        return this.http.get<ArquivadasPageResponse>(`${this.PATH}/solicitacoes/arquivadas`, { params, context });
    }

    /** Total, concluídos no ano e tempo médio do arquivo. Não consulta o portal. */
    resumoArquivadas(idServico: number, uf: string, context?: HttpContext): Observable<ArquivadasResumoResponse> {
        const params = new HttpParams().set('idServico', idServico).set('uf', uf);
        return this.http.get<ArquivadasResumoResponse>(`${this.PATH}/solicitacoes/arquivadas/resumo`, { params, context });
    }
}
